import React from "react";
import { Input } from "@/components/ui/input";
import { ClipboardList, TrendingUp, TrendingDown, AlertTriangle, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ragOf, RAG_LABEL, RAG_TEXT_DARK, previousScore } from "@/lib/rag";
import { sectionStatus, SCORING_NOTE } from "@/lib/auditStatus";

const today = () => new Date().toISOString().slice(0, 10);
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

const RAG_DOT = { green: "bg-rag-green", amber: "bg-rag-amber", red: "bg-rag-red" };
const RAG_TEXT = { green: "text-rag-green", amber: "text-rag-amber", red: "text-rag-red" };
const RAG_WORD = { green: "Green — meeting standard", amber: "Amber — partially met, improvement needed", red: "Red — significant gaps, priority action" };
const fmtPts = (n) => (Number.isInteger(n) ? n : n.toFixed(1));
const PRACTICE_WORD = { met: "Met", attention: "Needs attention", not_met: "Not met" };



export const openCount = (findings, plan) => findings.filter((f) => !(plan[f.key] || {}).completed_at).length;

export function ActionPlanTable({ findings, plan, onPlan, compact = false, highlightMissing = false }) {
  const open = openCount(findings, plan);
  return (
      <div className={`rounded-2xl bg-cream-card border border-cream-deep ${compact ? "" : "mt-4"} overflow-hidden`} data-testid="action-plan">
        <div className="px-5 py-3 border-b border-cream-deep flex items-center justify-between">
          <div className="label-caps text-muted-foreground">Action plan</div>
          <span className="text-xs text-muted-foreground" data-testid="action-plan-count">{open} open action{open === 1 ? "" : "s"}{findings.length !== open ? ` · ${findings.length - open} done` : ""}</span>
        </div>
        {findings.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="action-plan-empty">No open actions — areas marked Needs attention / Not met and resident items marked No / Partially will appear here automatically.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-muted-foreground bg-cream">
                <th className="px-4 py-2 font-semibold">Finding</th><th className="px-4 py-2 font-semibold">Action</th><th className="px-4 py-2 font-semibold">Owner</th><th className="px-4 py-2 font-semibold">Deadline</th><th className="px-4 py-2 font-semibold">Follow-up / re-audit</th><th className="px-4 py-2 font-semibold">Completed</th>
              </tr></thead>
              <tbody className="divide-y divide-cream">
                {findings.map((f) => {
                  const p = plan[f.key] || {};
                  const done = !!p.completed_at;
                  return (
                    <tr key={f.key} data-testid={`finding-${f.key}`} className={done ? "opacity-70" : (!p.owner || !p.deadline) && highlightMissing ? "bg-rag-amber-bg/60" : ""}>
                      <td className="px-4 py-2 align-top min-w-[220px]">
                        <div className="text-xs text-muted-foreground">{f.scope} · {f.domain}</div>
                        <div className="font-medium text-brand">{f.item} <span className={`text-xs font-semibold ${f.answer === "no" ? "text-rag-red" : "text-rag-amber"}`}>({f.key.startsWith("area:") ? (f.answer === "no" ? "Not met" : "Needs attention") : (f.answer === "no" ? "No" : "Partially")})</span></div>
                      </td>
                      <td className="px-4 py-2 align-top min-w-[180px] text-sm">{f.action || <span className="text-rag-red text-xs">Action required — add it on the item above</span>}</td>
                      <td className="px-4 py-2 align-top"><Input data-testid={`plan-owner-${f.key}`} placeholder="Named owner" defaultValue={p.owner || ""} onBlur={(e) => onPlan(f.key, "owner", e.target.value)} className={`h-8 text-xs min-w-[130px] bg-white ${highlightMissing && !done && !p.owner ? "border-rag-amber" : ""}`} /></td>
                      <td className="px-4 py-2 align-top"><Input data-testid={`plan-deadline-${f.key}`} type="date" value={p.deadline || ""} onChange={(e) => onPlan(f.key, "deadline", e.target.value)} className={`h-8 text-xs bg-white ${highlightMissing && !done && !p.deadline ? "border-rag-amber" : ""}`} /></td>
                      <td className="px-4 py-2 align-top"><Input data-testid={`plan-reaudit-${f.key}`} type="date" value={p.reaudit || ""} onChange={(e) => onPlan(f.key, "reaudit", e.target.value)} className="h-8 text-xs bg-white" /></td>
                      <td className="px-4 py-2 align-top">
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" data-testid={`plan-done-${f.key}`} checked={done} onChange={(e) => onPlan(f.key, "completed_at", e.target.checked ? today() : "")} className="h-4 w-4 accent-[#166534]" />
                          <span className={done ? "text-rag-green font-semibold" : "text-muted-foreground"}>{done ? `Done ${fmtShort(p.completed_at)}` : "Mark done"}</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}

export function SignOffPanel({ onComplete, completing, compact = false, disabled = false, reason = null }) {
  return (
        <div className={`${compact ? "mt-4" : "mt-4 mb-24"} flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-cream-card border border-cream-deep p-5`} data-testid="signoff-panel">
          <div>
            <div className="font-heading font-bold text-brand text-sm">Approve & sign off this quarter's review</div>
            <p className="text-xs text-muted-foreground mt-0.5">Every open action needs an owner and deadline. Signing off records the score, date and your name in the Evidence Pack.</p>
            {disabled && reason && <p className="text-xs font-semibold text-rag-red mt-1" data-testid="signoff-blocked-reason">{reason}</p>}
          </div>
          <Button data-testid="signoff-btn" onClick={onComplete} disabled={completing || disabled} className="rounded-full bg-coral hover:bg-coral-hover text-white disabled:opacity-60">
            <ClipboardCheck className="h-4 w-4 mr-2" />{completing ? "Recording…" : "Approve & sign off"}
          </Button>
        </div>
  );
}

export function AuditSummary({ live, assessed = true, complete = true, tpl, findings, plan, onPlan, history = [], onComplete, completing }) {
  const inProgress = assessed && !complete;
  const prev = previousScore(history);
  const diff = prev === null ? null : live.overall - prev;
  const band = ragOf(live.overall);
  const titles = Object.fromEntries(tpl.map((d) => [d.key, d.title]));
  const open = openCount(findings, plan);

  return (
    <section id="audit-summary" className="mt-10 scroll-mt-24" data-testid="audit-summary">
      <div className="flex items-center gap-2 mb-4"><ClipboardList className="h-4 w-4 text-brand" /><h4 className="font-heading font-bold text-brand text-sm">Step 3 — Approve the action plan & sign off</h4></div>

      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        <div className="rounded-2xl bg-brand text-white p-5" data-testid="overall-score-card">
          <div className="label-caps text-white/60">{inProgress ? "Score for completed checks" : "Overall score"}</div>
          <div className="text-3xl font-bold font-heading mt-1" data-testid="overall-points">{assessed ? `${live.overall}%` : "—"}</div>
          <div className={`text-sm font-semibold mt-1 ${assessed && !inProgress ? RAG_TEXT_DARK[band] : "text-white/70"}`} data-testid="overall-pct">{!assessed ? "Not assessed" : inProgress ? "Assessment in progress" : RAG_LABEL[band]}</div>
          <div className="text-xs text-white/70 mt-2" data-testid="score-breakdown">
            Practice: {live.practicePct == null ? "not confirmed" : `${live.practicePct}%`} · Resident audit: {live.residentPct == null ? "not started" : `${live.residentPct}% (${fmtPts(live.points)}/${live.applicable} pts)`}
          </div>
          {diff !== null && (
            <div className="text-xs text-white/70 mt-3 inline-flex items-center gap-1" data-testid="overall-trend">
              {diff >= 0 ? <TrendingUp className="h-3.5 w-3.5 text-emerald-300" /> : <TrendingDown className="h-3.5 w-3.5 text-red-300" />}
              {diff >= 0 ? "+" : ""}{diff} pts vs last completed assessment ({prev}%)
            </div>
          )}
          {diff === null && <div className="text-xs text-white/60 mt-3">Complete this assessment to start tracking improvement between audits.</div>}
        </div>

        <div className="rounded-2xl bg-cream-card border border-cream-deep p-5" data-testid="rag-summary">
          <div className="label-caps text-muted-foreground mb-2">RAG summary by section</div>
          <ul className="divide-y divide-cream">
            {live.domains.map((d) => {
              const st = d.practice ? (d.answered ? { kind: "complete", label: PRACTICE_WORD[d.status] } : { kind: "not_started", label: "Not confirmed", cls: "text-muted-foreground" }) : sectionStatus(d);
              const complete = st.kind === "complete";
              return (
                <li key={d.key} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`rag-${d.key}`}>
                  <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${complete ? RAG_DOT[d.rag] : "bg-cream-deep"}`} />
                  <span className="font-medium text-brand min-w-[180px]">{d.title || titles[d.key]}{d.practice ? "" : <span className="text-muted-foreground font-normal text-xs"> · residents</span>}</span>
                  <span className={`font-bold ${complete ? RAG_TEXT[d.rag] : st.cls} min-w-[3rem]`}>{st.label}</span>
                  {d.hasNo && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-rag-red bg-rag-red-bg px-1.5 py-0.5 rounded"><AlertTriangle className="h-3 w-3" />Action required</span>}
                  {complete && <span className="text-muted-foreground text-xs hidden sm:inline">{RAG_WORD[d.rag]}</span>}
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="scoring-note">{SCORING_NOTE}</p>
        </div>
      </div>

      <ActionPlanTable findings={findings} plan={plan} onPlan={onPlan} />
      {onComplete && <SignOffPanel onComplete={onComplete} completing={completing} />}
    </section>
  );
}
